import React, { useState, useEffect } from 'react';
import GenericModal from '@components/common/modals/GenericModal';
import FileUploadZone from '@components/features/import/FileUploadZone';
import Button from '@components/common/Button';
import { showToast } from '@utils/toast';
import { ACCEPTED_ATTACHMENT_TYPES, MAX_ATTACHMENT_SIZE_MB } from './helpers';
import styles from './AttachmentSection.module.css';

interface AttachmentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (files: File[]) => void;
  isUploading: boolean;
}

const AttachmentUploadModal: React.FC<AttachmentUploadModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  isUploading,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!isOpen) {
      setSelectedFiles([]);
      setError('');
    }
  }, [isOpen]);

  const handleFilesSelect = (files: File[]) => {
    setSelectedFiles(files);
    setError('');
  };

  const handleValidationError = (errorMessage: string) => {
    showToast.error(errorMessage);
  };

  const handleFileRemove = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one file');
      return;
    }
    onUpload(selectedFiles);
  };

  const handleClose = () => {
    onClose();
  };

  const filePlural = selectedFiles.length === 1 ? 'File' : 'Files';
  const attachLabel = isUploading ? 'Uploading...' : `Attach ${selectedFiles.length} ${filePlural}`;

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Attach Files"
    >
      <div className={styles.uploadModalContent}>
        <FileUploadZone
          onFilesSelect={handleFilesSelect}
          onFileRemoveAt={handleFileRemove}
          selectedFiles={selectedFiles}
          acceptedTypes={ACCEPTED_ATTACHMENT_TYPES}
          maxSizeMB={MAX_ATTACHMENT_SIZE_MB}
          error={error}
          isProcessing={isUploading}
          multiple={true}
          title="Drop files here or click to browse"
          subtitle="PDF, DOCX, XLSX, JPG, PNG up to 10MB each"
          onValidationError={handleValidationError}
        />
        <div className={styles.uploadModalActions}>
          <Button variant="secondary" onClick={handleClose} disabled={isUploading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpload}
            disabled={selectedFiles.length === 0 || isUploading}
          >
            {attachLabel}
          </Button>
        </div>
      </div>
    </GenericModal>
  );
};

export default AttachmentUploadModal;
